import { useEffect } from 'react';
import type { AccessReason } from '../types/agent';

/** How often the gated screen re-reads the access, per reason: a verification link is clicked within minutes
 *  (the chat then opens by itself); a suspension is lifted by staff, rarely; a subscription opens once its order
 *  is paid (a free one at once, a bank transfer later). */
export const POLL_MS: Record<AccessReason, number> = {
  email_unverified: 5_000,
  organization_suspended: 30_000,
  no_organization: 30_000,
  no_subscription: 15_000,
  free_full: 15_000,
  trial_ended: 15_000,
  payment_overdue: 30_000,
};
export const POLL_DEFAULT_MS = 30_000;

/** While `active`, calls `check` on the reason's cadence until it answers true (the chat opened) or the
 *  screen goes away. One call in flight at a time. */
export function useAccessPolling(active: boolean, reason: AccessReason | null, check: () => Promise<boolean>): void {
  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let busy = false;
    const tick = async () => {
      if (stopped || busy) return;
      busy = true;
      try {
        if (await check()) stopped = true;
      } catch {
        /* the next tick tries again */
      } finally {
        busy = false;
      }
    };
    const interval = window.setInterval(() => void tick(), reason ? POLL_MS[reason] : POLL_DEFAULT_MS);
    return () => {
      stopped = true;
      window.clearInterval(interval);
    };
  }, [active, reason, check]);
}
